import * as React from 'react';
import { Dialog, DialogType, DialogFooter, PrimaryButton, DefaultButton, Dropdown, TextField, MessageBar, MessageBarType, Spinner, SpinnerSize } from '@fluentui/react';
import type { IDropdownOption } from '@fluentui/react';

import { PBTCancelReasonOptions, PBTMessages, CaseActionNotesMinLength } from '../../../Services/PBTConstants';

export interface ICancelCaseDialogProps {
  isOpen: boolean;
  caseName: string;
  termEmailSent?: boolean;
  onConfirm: (reason: string, reasonText?: string) => Promise<void>;
  onDismiss: () => void;
}

export const CancelCaseDialog: React.FC<ICancelCaseDialogProps> = ({ isOpen, caseName, termEmailSent, onConfirm, onDismiss }) => {
  const [reason, setReason] = React.useState<string>('');
  const [reasonText, setReasonText] = React.useState<string>('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // The parent closes the dialog (isOpen -> false) on a successful confirm
  // without calling handleDismiss, so reset the fields whenever it closes to
  // avoid carrying stale reason/notes into the next open.
  React.useEffect(() => {
    if (!isOpen) {
      setReason('');
      setReasonText('');
      setError(null);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const isOther = reason === 'Other';
  const trimmedNotes = reasonText.trim();
  const isValid = reason !== '' && (!isOther || trimmedNotes.length >= CaseActionNotesMinLength);
  const notesError =
    isOther && trimmedNotes.length > 0 && trimmedNotes.length < CaseActionNotesMinLength
      ? PBTMessages.CaseActionNotesTooShort(CaseActionNotesMinLength)
      : undefined;

  const handleConfirm = async () => {
    if (!isValid) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await onConfirm(reason, isOther ? trimmedNotes : undefined);
    } catch (err) {
      setError((err as Error).message || 'Failed to cancel case.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDismiss = () => {
    setReason('');
    setReasonText('');
    setError(null);
    onDismiss();
  };

  return (
    <Dialog
      hidden={!isOpen}
      onDismiss={handleDismiss}
      dialogContentProps={{
        type: DialogType.normal,
        title: 'Cancel Case',
        subText: `Cancelling "${caseName}" will permanently stop all pending actions, including scheduled emails and offboarding case creation. This action cannot be undone.`,
      }}
      modalProps={{ isBlocking: true }}
      minWidth={480}
    >
      <MessageBar messageBarType={MessageBarType.severeWarning} styles={{ root: { marginBottom: 12 } }}>
        This action is <strong>irreversible</strong>. All scheduled emails, checks, and offboarding actions will be permanently cancelled.
      </MessageBar>

      {termEmailSent && (
        <MessageBar messageBarType={MessageBarType.warning} styles={{ root: { marginBottom: 12 } }}>
          The termination email has already been sent to the employee. Cancelling this case will not recall the email. Manual follow-up may be required.
        </MessageBar>
      )}

      {error && (
        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
          {error}
        </MessageBar>
      )}

      <Dropdown
        label="Cancel Reason"
        required
        placeholder="Select a reason"
        options={PBTCancelReasonOptions as unknown as IDropdownOption[]}
        selectedKey={reason}
        onChange={(_, option) => setReason((option?.key as string) || '')}
        disabled={isSubmitting}
      />

      {isOther && (
        <TextField
          label="Additional Notes"
          required
          multiline
          rows={3}
          value={reasonText}
          onChange={(_, v) => setReasonText(v || '')}
          placeholder="Please provide details..."
          description={`Minimum ${CaseActionNotesMinLength} characters.`}
          errorMessage={notesError}
          disabled={isSubmitting}
        />
      )}

      <DialogFooter>
        {isSubmitting ? (
          <Spinner size={SpinnerSize.small} label="Cancelling..." />
        ) : (
          <>
            <PrimaryButton text="Confirm Cancel" onClick={handleConfirm} disabled={!isValid} styles={{ root: { backgroundColor: '#a4262c', borderColor: '#a4262c' } }} />
            <DefaultButton text="Go Back" onClick={handleDismiss} />
          </>
        )}
      </DialogFooter>
    </Dialog>
  );
};
